import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("timeout"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
      } catch (e) {
        console.warn("Payment status poll failed, will retry", e);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params]);

  return (
    <AppShell active="bookings">
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <div className="w-16 h-16 rounded-full zenith-cta animate-breathe" />
            <p className="mt-6 font-headline text-primary text-xl">Confirming your payment…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-20 h-20 text-primary" strokeWidth={1.2} />
            <h1 className="mt-6 font-headline text-4xl font-light text-primary">You're all set 🌿</h1>
            <p className="mt-3 text-on-surface-variant max-w-sm">Your session is confirmed. A WhatsApp & email confirmation with a calendar invite is on the way.</p>
            <button onClick={() => navigate("/app/bookings")} className="mt-8 px-7 py-4 rounded-full zenith-cta text-white font-medium hover:opacity-90 transition-opacity duration-300" data-testid="view-bookings-button">
              View my sessions
            </button>
          </>
        )}
        {state === "timeout" && (
          <>
            <AlertCircle className="w-20 h-20 text-secondary" strokeWidth={1.2} />
            <h1 className="mt-6 font-headline text-3xl font-light text-primary">Still processing</h1>
            <p className="mt-3 text-on-surface-variant max-w-sm">Your payment is taking a moment. Check your sessions shortly.</p>
            <button onClick={() => navigate("/app/bookings")} className="mt-8 px-7 py-4 rounded-full bg-surface-container-lowest text-primary font-medium shadow-ambient" data-testid="view-bookings-button">
              Go to my sessions
            </button>
          </>
        )}
      </div>
    </AppShell>
  );
}
